import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, RefreshCw, Share2, XCircle } from 'lucide-react';
import {
  LinkedInStatus,
  getLinkedInStatus,
  revokeLinkedInToken,
  startLinkedInOAuth,
} from '../lib/api/linkedin.js';

/**
 * LinkedIn connection — the one recurring human step in the Community
 * Management API integration (LINKEDIN_CM_API_INTEGRATION_PLAN §4.1).
 *
 * Connect runs the 3-legged consent as the signed-in operator (D1: the
 * founder's account, Super admin on every masthead Page). LinkedIn sends the
 * browser back to /linkedin/callback, which exchanges the code through the
 * backend. Re-consent is due once a year (the refresh token's fixed clock);
 * the due date is shown permanently here and alerts fire at −30 days.
 */
export function LinkedInSettingsPage() {
  const [status, setStatus] = useState<LinkedInStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await getLinkedInStatus());
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load LinkedIn status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      const { authorization_url } = await startLinkedInOAuth();
      window.location.assign(authorization_url);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Could not start the LinkedIn consent flow.');
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Revoke the stored LinkedIn token? URN resolution and tagging stop until you reconnect.')) return;
    setBusy(true);
    try {
      setStatus(await revokeLinkedInToken());
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Revoke failed.');
    } finally {
      setBusy(false);
    }
  };

  const tok = status?.token ?? null;
  const reconsentSoon = tok?.reconsent_due_in_days != null && tok.reconsent_due_in_days <= 30;

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Share2 className="h-6 w-6 text-slate-600" />
          <div>
            <h1 className="text-2xl font-semibold">LinkedIn connection</h1>
            <p className="text-sm text-slate-500">
              Community Management API · version {status?.api_version ?? '…'} · app token for URN resolution, tagging and analytics
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4" /> {error}
        </div>
      )}

      {!loading && status && !status.client_id_configured && (
        <div className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          The backend has no LinkedIn client id configured (ASSAYER_LINKEDIN_CLIENT_ID). Deploy with the secrets before connecting.
        </div>
      )}

      <section className="mb-6 rounded-lg border bg-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {status?.connected ? (
              <CheckCircle className="h-5 w-5 text-emerald-600" />
            ) : (
              <XCircle className="h-5 w-5 text-slate-400" />
            )}
            <span className="font-medium">{status?.connected ? 'Connected' : 'Not connected'}</span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={connect}
              disabled={busy || !status?.client_id_configured}
              className="rounded bg-slate-900 px-3 py-1.5 text-sm text-white disabled:opacity-50"
            >
              {status?.connected ? 'Reconnect' : 'Connect'}
            </button>
            {status?.connected && (
              <button type="button" onClick={revoke} disabled={busy} className="rounded border px-3 py-1.5 text-sm">
                Revoke
              </button>
            )}
          </div>
        </div>

        {tok && (
          <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <dt className="text-slate-500">Member</dt>
            <dd className="font-mono text-xs">{tok.member_urn ?? '—'}</dd>
            <dt className="text-slate-500">Consented</dt>
            <dd>{tok.consented_at ? new Date(tok.consented_at).toLocaleString() : '—'} {tok.consented_by ? `· ${tok.consented_by}` : ''}</dd>
            <dt className="text-slate-500">Scopes</dt>
            <dd className="font-mono text-xs">{tok.scopes}</dd>
            <dt className="text-slate-500">Access token expires</dt>
            <dd>{new Date(tok.access_expires_at).toLocaleDateString()} ({tok.access_expires_in_days} days) — refreshed automatically</dd>
            <dt className="text-slate-500">Re-consent due</dt>
            <dd className={reconsentSoon ? 'font-medium text-amber-700' : ''}>
              {tok.refresh_expires_at
                ? `${new Date(tok.refresh_expires_at).toLocaleDateString()} (${tok.reconsent_due_in_days} days)`
                : 'No refresh token — reconnect before the access token expires'}
            </dd>
            <dt className="text-slate-500">Last used</dt>
            <dd>{tok.last_used_at ? new Date(tok.last_used_at).toLocaleString() : '—'}</dd>
            {tok.last_error && (
              <>
                <dt className="text-slate-500">Last error</dt>
                <dd className="text-red-700">{tok.last_error}</dd>
              </>
            )}
          </dl>
        )}
        <p className="mt-3 text-xs text-slate-500">Redirect URL registered on the app: <span className="font-mono">{status?.redirect_uri}</span></p>
      </section>

      <section className="mb-6 rounded-lg border bg-white p-4">
        <h2 className="mb-2 font-medium">Masthead Pages</h2>
        <p className="mb-3 text-xs text-slate-500">
          Posting, images and statistics need the token's member to be a Super admin on each Page. Checked at connect and daily.
        </p>
        <ul className="divide-y text-sm">
          {(status?.mastheads ?? []).map((m) => (
            <li key={m.target_id} className="flex items-center justify-between py-2">
              <span>
                {m.name ?? m.target_id} <span className="ml-2 font-mono text-xs text-slate-500">{m.linkedin_urn}</span>
              </span>
              {m.administered == null ? (
                <span className="text-xs text-slate-400">not connected</span>
              ) : m.administered ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle className="h-4 w-4" /> administrator</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-red-700"><XCircle className="h-4 w-4" /> role missing</span>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-lg border bg-white p-4">
        <h2 className="mb-2 font-medium">Quota used today (UTC)</h2>
        <p className="mb-3 text-xs text-slate-500">
          LinkedIn sends no quota headers; this ledger is the only counter. Development Tier: 500/app and 100/user per endpoint per day, 2/day for batch lookups.
          Queue resolution is capped at {status?.resolve_budget ?? '…'} finder calls a day so push-time lookups always have room.
        </p>
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr><th className="py-1">Endpoint</th><th className="py-1 text-right">Used</th><th className="py-1 text-right">Cap</th></tr>
          </thead>
          <tbody>
            {(status?.quota_today_utc ?? []).filter((q) => q.used_today > 0 || q.endpoint_key === 'organizations-FINDER').map((q) => (
              <tr key={q.endpoint_key} className="border-t">
                <td className="py-1 font-mono text-xs">{q.endpoint_key}</td>
                <td className="py-1 text-right">{q.used_today}</td>
                <td className="py-1 text-right">{q.cap ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
